import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {AbortController | null} */
  #swipeAbortController = null;

  /** @type {number} */
  #swipeStartY = 0;

  /** @type {number} */
  #swipeStartTime = 0;

  /** @type {number} */
  #swipeCurrentY = 0;

  /** @type {boolean} */
  #isSwiping = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#attachSwipeListeners);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#detachSwipeListeners);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#attachSwipeListeners);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#detachSwipeListeners);
    this.#historyAbortController?.abort();
    this.#detachSwipeListeners();
  }

  #attachSwipeListeners = () => {
    if (!isMobileBreakpoint()) return;
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#swipeAbortController = new AbortController();
    const { signal } = this.#swipeAbortController;
    dialog.addEventListener('touchstart', this.#onTouchStart, { passive: true, signal });
  };

  #detachSwipeListeners = () => {
    this.#swipeAbortController?.abort();
    this.#swipeAbortController = null;
    const { dialog } = this.refs;
    if (dialog) {
      dialog.style.transform = '';
      dialog.style.transition = '';
    }
  };

  #onTouchStart = (/** @type {TouchEvent} */ event) => {
    const touch = event.touches[0];
    this.#swipeStartY = touch.clientY;
    this.#swipeStartTime = Date.now();
    this.#swipeCurrentY = 0;
    this.#isSwiping = false;

    const { dialog } = this.refs;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    if (content instanceof HTMLElement && content.scrollTop > 0) return;

    const { signal } = this.#swipeAbortController;
    dialog.addEventListener('touchmove', this.#onTouchMove, { passive: false, signal });
    dialog.addEventListener('touchend', this.#onTouchEnd, { passive: true, signal });
    dialog.addEventListener('touchcancel', this.#onTouchCancel, { passive: true, signal });
  };

  #onTouchMove = (/** @type {TouchEvent} */ event) => {
    const touch = event.touches[0];
    const deltaY = touch.clientY - this.#swipeStartY;

    if (deltaY <= 0) return;

    event.preventDefault();
    this.#isSwiping = true;
    this.#swipeCurrentY = deltaY;

    const { dialog } = this.refs;
    if (!dialog) return;

    const damped = deltaY > 80 ? 80 + (deltaY - 80) * 0.3 : deltaY;
    dialog.style.transition = 'none';
    dialog.style.transform = `translateY(${damped}px)`;
  };

  #onTouchEnd = () => {
    if (!this.#isSwiping) return;
    this.#isSwiping = false;

    const { dialog } = this.refs;
    if (!dialog) return;

    const elapsed = Date.now() - this.#swipeStartTime;
    const velocity = elapsed > 0 ? this.#swipeCurrentY / elapsed : 0;
    const drawerHeight = dialog.getBoundingClientRect().height;

    if (velocity > 0.4 || this.#swipeCurrentY > drawerHeight * 0.35) {
      dialog.style.transition = 'transform 0.25s cubic-bezier(0.23, 1, 0.32, 1)';
      dialog.style.transform = 'translateY(100%)';
      dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      dialog.addEventListener('transitionend', () => {
        dialog.style.transform = '';
        dialog.style.transition = '';
        this.closeDialog().then(() => {
          dialog.style.removeProperty('--dialog-drawer-closing-animation');
        });
      }, { once: true });
    } else {
      dialog.style.transition = 'transform 0.35s cubic-bezier(0.23, 1, 0.32, 1)';
      dialog.style.transform = 'translateY(0)';
      dialog.addEventListener('transitionend', () => {
        dialog.style.transform = '';
        dialog.style.transition = '';
      }, { once: true });
    }

    this.#swipeCurrentY = 0;
  };

  #onTouchCancel = () => {
    this.#isSwiping = false;
    this.#swipeCurrentY = 0;
    const { dialog } = this.refs;
    if (!dialog) return;
    dialog.style.transition = 'transform 0.3s cubic-bezier(0.23, 1, 0.32, 1)';
    dialog.style.transform = 'translateY(0)';
    dialog.addEventListener('transitionend', () => {
      dialog.style.transform = '';
      dialog.style.transition = '';
    }, { once: true });
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
